import React, { useEffect, useId, useRef, useState } from 'react';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { useDirtyDismiss } from './ui/useDirtyDismiss';

export type OrderAction = 'cancel' | 'collect' | 'refund' | 'retry';
const labels = { cancel: 'Cancelar pedido', collect: 'Registrar cobro', refund: 'Registrar reembolso', retry: 'Reintentar entrega' };
export const orderConsequences = (order: any, action: OrderAction) => {
  if (action === 'refund') return 'Registra un reembolso total en la contabilidad. No ejecuta una transferencia bancaria ni entrega efectivo. Gestiona la devolución por el medio correspondiente. No repone inventario ni cambia el estado de cocina.';
  if (action === 'collect') return order.paymentMethod === 'Transferencia'
    ? 'Registra el pedido como cobrado solo después de verificar el ingreso y su referencia. Esta aplicación no ejecuta transferencias bancarias. No modifica el inventario ni el estado de cocina.'
    : 'Registra el pedido como cobrado en efectivo. Confirma únicamente si ya recibiste el dinero. No modifica el inventario ni el estado de cocina.';
  if (action === 'retry') return 'El pedido vuelve a la cocina como pendiente. Este cambio no descuenta insumos adicionales ni modifica el registro de pago.';
  return (order.status === 'listo'
    ? 'El pedido se cancela sin reponer inventario: los insumos preparados quedan registrados como merma. '
    : 'El pedido se cancela y sus insumos se devuelven al inventario, una sola vez. ')
    + 'Se libera el uso del cupón, si lo hubo. '
    + (order.paymentStatus === 'collected'
      ? 'El cobro se conserva. Cancelar no registra un reembolso; si corresponde, regístralo por separado.'
      : 'Este cambio no registra cobros ni reembolsos.');
};

export const OrderActionDialog = ({ order, action, onConfirm, onClose }: {
  order: any; action: OrderAction; onConfirm: (data: { reason: string; reference: string }) => Promise<void>; onClose: () => void;
}) => {
  const id = useId();
  const [stage, setStage] = useState<'edit' | 'review'>('edit');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  const { requestClose, discardConfirmation } = useDirtyDismiss({ dirty: Boolean(reason || reference), busy, onDiscard: onClose });
  const needsReference = action === 'collect' && order.paymentMethod === 'Transferencia';
  const hasReason = action === 'refund' || action === 'cancel' || action === 'retry';
  const amount = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(order.totalAmount || 0);
  return <>
    <Dialog labelledBy={id} onClose={requestClose} busy={busy} focusKey={stage} className="flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={requestClose} />
      <form className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl bg-white dark:bg-slate-950 p-5 sm:p-8 space-y-5"
        onSubmit={async event => {
          event.preventDefault();
          if (lock.current) return;
          if (stage === 'edit') {
            if (needsReference && reference.trim().length < 4) { setError('Introduce una referencia verificada de al menos 4 caracteres.'); return; }
            if (action === 'refund' && reason.trim().length < 4) { setError('Explica el motivo del reembolso con al menos 4 caracteres.'); return; }
            setError(''); setStage('review'); return;
          }
          lock.current = true; setBusy(true); setError('');
          try { await onConfirm({ reason: reason.trim(), reference: reference.trim() }); onClose(); }
          catch (failure: any) { setError(failure?.response?.data?.message || 'No se pudo completar. Los datos se conservan; revisa e intenta de nuevo.'); }
          finally { lock.current = false; setBusy(false); }
        }}>
        <h2 id={id} tabIndex={-1} className="text-xl font-black break-words">{stage === 'review' ? 'Revisar: ' : ''}{labels[action]} #{order.orderNumber || order._id}</h2>
        <p className="font-bold break-words">{order.delivery?.customerName || order.tableNumber || order.customerName || 'Pedido'} · {amount} · {order.paymentMethod}</p>
        <p className="text-sm">{orderConsequences(order, action)}</p>
        {error && <p role="alert" ref={errorRef} tabIndex={-1} className="text-red-700 dark:text-red-300">{error}</p>}
        <fieldset disabled={busy} className="space-y-4">
          {stage === 'edit' ? <>
            {needsReference && <label className="block font-bold" htmlFor={id + '-reference'}>Referencia de transferencia verificada (obligatoria)
              <input id={id + '-reference'} required minLength={4} maxLength={200} value={reference} onChange={e => setReference(e.target.value)} className="block w-full min-h-[44px] rounded-xl bg-slate-100 dark:bg-slate-900 p-3" />
            </label>}
            {hasReason && <label className="block font-bold" htmlFor={id + '-reason'}>Motivo {action === 'refund' ? '(obligatorio, mínimo 4 caracteres)' : '(opcional)'}
              <textarea id={id + '-reason'} required={action === 'refund'} minLength={action === 'refund' ? 4 : undefined} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} className="block w-full rounded-xl bg-slate-100 dark:bg-slate-900 p-3" />
            </label>}
          </> : <div className="rounded-xl bg-slate-100 dark:bg-slate-900 p-4 space-y-2 text-sm">
            <p className="font-bold">Revisión final · {amount}</p>
            {needsReference && <p className="break-words">Referencia verificada: {reference}</p>}
            {hasReason && <p className="break-words">Motivo: {reason || 'Sin motivo adicional'}</p>}
            <p>Confirma solo si el pedido, el importe y las consecuencias son correctos.</p>
          </div>}
          <div className="flex flex-col gap-3">
            <Button type="button" variant="outline" onClick={requestClose}>Volver al pedido</Button>
            {stage === 'review' && <Button type="button" variant="outline" onClick={() => { setError(''); setStage('edit'); }}>Editar datos</Button>}
            <Button type="submit" className="h-auto min-h-[44px] whitespace-normal py-3">{busy ? 'Procesando…' : stage === 'edit' ? 'Revisar operación' : 'Confirmar: ' + labels[action].toLowerCase()}</Button>
          </div>
        </fieldset>
      </form>
    </Dialog>
    {discardConfirmation}
  </>;
};
